import { DOCUMENT } from '@angular/common';
import { Component, EventEmitter, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  DwButtonConfig, DwLookupInfo, DwMetaDataService, DwMetaDataServiceToken, DwModalConfig, DwModalHandler, DwModalHostComponent, DwModalResult,
  DwSelectLookupOptions
} from '@devwareapps/devware-cap';
import { defer, Observable, of, Subject } from 'rxjs';
import { defaultIfEmpty, finalize, map, takeUntil } from 'rxjs/operators';
import { AppMetaDataLookups } from '../../../../meta-data/app-meta-data.service';
import { AdminManagerService } from '../../services/admin-manager.service';

@Component({
  selector: 'app-school-selection-modal',
  template: `
    <div class="ssm-body">
      <p class="ssm-description">
        Pick the flight school you want to manage.
      </p>
      <div class="ssm-current">
        <span class="ssm-avatar" aria-hidden="true">{{ currentSchoolInitials }}</span>
        <div class="ssm-current-text">
          <span class="ssm-eyebrow">Currently managing</span>
          <span class="ssm-current-name" [title]="currentSchoolName">{{ currentSchoolName }}</span>
        </div>
      </div>
      <fieldset class="ssm-field" [disabled]="saving" [class.is-saving]="saving">
        <label id="ssm-school-label" class="ssm-label">Switch to</label>
        <dw-search-select-lookup *ngIf="schoolLookupItems$ | async as schoolLookupItems"
          [lookupId]="fligtSchoolLookupId" [overrideValues]="schoolLookupItems" [(ngModel)]="flightSchoolId"
          (ngModelChange)="onSchoolChange()" [options]="lookupOptions" placeholder="Select a school"
          fieldLabel="Switch to" [showBlankOption]="false" [showClear]="false" [alwaysShowFilter]="true" [virtualScrollItems]="36">
        </dw-search-select-lookup>
        <p class="ssm-hint" aria-live="polite">{{ isUnchanged ? "You're already managing this school." : '' }}</p>
      </fieldset>
      <div *ngIf="saveError" class="ssm-error" role="alert">
        Couldn't switch schools. Check your connection and try again.
      </div>
    </div>
    <div class="ssm-footer">
      <button type="button" class="btn btn-secondary" [disabled]="saving" (click)="cancel()">Cancel</button>
      <button type="button" class="btn btn-primary" [disabled]="!flightSchoolId || isUnchanged || saving"
        [attr.aria-busy]="saving" (click)="save()">
        <i *ngIf="saving" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
        {{ saving ? 'Switching…' : 'Switch school' }}
      </button>
    </div>
  `
})
export class SchoolSelectionModalComponent implements OnInit, OnDestroy, DwModalHandler {
  readonly fligtSchoolLookupId = AppMetaDataLookups.FlightSchoolActiveOnly;
  readonly lookupOptions: DwSelectLookupOptions = { displayStyle: 'standard' };
  readonly closeModal = new EventEmitter<DwModalResult>();
  modalConfig: DwModalConfig;
  flightSchoolId: number | string;
  currentFlightSchoolId: number;
  currentSchoolName: string;
  currentSchoolInitials: string;
  schoolLookupItems$: Observable<DwLookupInfo[]>;
  isUnchanged = true;
  saving = false;
  saveError = false;

  private readonly destroy$ = new Subject<void>();
  // PrimeNG installs its Escape listener once and does not recheck closeOnEscape.
  private readonly preventSavingDismissal = (event: KeyboardEvent): void => {
    if (this.saving && event.key === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };

  constructor(
    private readonly adminManagerService: AdminManagerService,
    @Inject(DwMetaDataServiceToken) private readonly metaDataService: DwMetaDataService,
    private readonly modalHost: DwModalHostComponent,
    @Inject(DOCUMENT) private readonly document: Document
  ) { }

  ngOnInit(): void {
    const aviatorUser = this.adminManagerService.getAviatorUser();
    this.currentFlightSchoolId = aviatorUser.FlightSchoolId;
    this.flightSchoolId = this.currentFlightSchoolId;
    this.currentSchoolName = aviatorUser.FlightSchoolName || 'Current school';
    this.currentSchoolInitials = this.currentSchoolName.trim().split(/\s+/).slice(0, 2)
      .map(word => word.charAt(0).toUpperCase()).join('');
    this.currentSchoolName = this.currentSchoolName === 'Aviator Pro' ? 'AviatorPro' : this.currentSchoolName;
    this.schoolLookupItems$ = this.metaDataService.getLookup(this.fligtSchoolLookupId).pipe(
      map(items => items.map(item => ({
        ...item,
        Display: item.Display === 'Aviator Pro' ? 'AviatorPro' : item.Display
      })))
    );
    this.onSchoolChange();
    this.document.addEventListener('keydown', this.preventSavingDismissal, true);
  }

  onSchoolChange(): void {
    // CAP lookup values can be strings even when the user context id is numeric.
    this.isUnchanged = !!this.flightSchoolId && String(this.flightSchoolId) === String(this.currentFlightSchoolId);
    this.saveError = false;
  }

  save(): void {
    if (!this.flightSchoolId || this.isUnchanged || this.saving) {
      return;
    }

    this.setSaving(true);
    this.saveError = false;
    defer(() => this.adminManagerService.setCurrentFlightSchool(this.flightSchoolId))
      .pipe(
        defaultIfEmpty(null),
        takeUntil(this.destroy$),
        finalize(() => this.setSaving(false))
      )
      .subscribe(() => this.closeModal.emit(this.createResult(false)), () => this.saveError = true);
  }

  cancel(): void {
    if (!this.saving) {
      this.closeModal.emit(this.createResult(true));
    }
  }

  buttonClick(_buttonConfig: DwButtonConfig): Observable<DwModalResult> {
    return of(this.createResult(true));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.document.removeEventListener('keydown', this.preventSavingDismissal, true);
  }

  private setSaving(saving: boolean): void {
    this.saving = saving;
    this.modalConfig.preventClose = saving;
    // CAP caches these flags on creation rather than reading preventClose dynamically.
    const instance = this.modalHost.modalInstances.find(modal => modal.modalConfig === this.modalConfig);
    if (instance) {
      instance.closeable = !saving;
      instance.closeOnEscape = !saving;
    }
  }

  private createResult(cancelled: boolean): DwModalResult {
    const result = new DwModalResult();
    result.cancelled = cancelled;
    result.closeModal = true;
    return result;
  }
}
